'use client';

/**
 * Full-viewport rotating hero — streaming-service style. Ported from
 * ThemeMaker's {@code blocks/hero/HeroCinematic}.
 *
 * <h3>Behaviour</h3>
 * <ul>
 *   <li>Full-bleed background image, dark gradient from left for
 *       legibility.</li>
 *   <li>Content (kicker, title, subtitle, CTA) pinned bottom-left or
 *       centre.</li>
 *   <li>Slide dots at the bottom; click a dot to jump.</li>
 *   <li>Auto-rotates every {@code intervalSeconds} (min 2s).</li>
 *   <li>Pauses rotation on hover for accessibility.</li>
 *   <li>Ken Burns animation on the active backdrop, suppressed
 *       under {@code prefers-reduced-motion}.</li>
 * </ul>
 */
import { useEffect, useMemo, useState } from 'react';
import { tx, asList } from '../_shared/coerce';
import { HeroEmptyFallback } from '../_shared/HeroEmptyFallback';
import {
  PortalLink,
  isVisible,
  useCurrentUser,
  type CurrentUser,
} from '@akademi/site-builder-runtime';
import type { CampaignAudience } from '../_shared/campaignAudience';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

export interface HeroCinematicSlide extends CampaignAudience {
  kicker?: Localized;
  title?: Localized;
  subtitle?: Localized;
  ctaLabel?: Localized;
  ctaHref?: string;
  backgroundImage?: string;
  align?: 'left' | 'center';
  /** Optional accent colour for the kicker (defaults to brand). */
  accent?: string;
}

export interface HeroCinematicProps {
  slides?: HeroCinematicSlide[];
  /** {@code medium} → 60vh, {@code large} → 80vh (default),
   *  {@code full} → 100vh. */
  height?: 'medium' | 'large' | 'full';
  intervalSeconds?: number;
  autoRotate?: boolean;
}

export function HeroCinematic({
  slides,
  height = 'large',
  intervalSeconds = 6,
  autoRotate = true,
}: HeroCinematicProps) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const currentUser = useCurrentUser<CurrentUser>();
  const list = useMemo(
    () => asList<HeroCinematicSlide>(slides).filter((s) => isVisible(s, currentUser)),
    [slides, currentUser],
  );

  useEffect(() => {
    if (!autoRotate || paused || list.length < 2) return;
    const id = setInterval(
      () => {
        setActive((i) => (i + 1) % list.length);
      },
      Math.max(2, intervalSeconds) * 1000,
    );
    return () => clearInterval(id);
  }, [autoRotate, paused, intervalSeconds, list.length]);

  const heightStyle =
    height === 'full'
      ? 'min(100vh, 900px)'
      : height === 'medium'
        ? 'min(60vh,  560px)'
        : 'min(80vh,  720px)';

  if (list.length === 0) {
    return <HeroEmptyFallback style={{ width: '100%', height: heightStyle }} />;
  }

  return (
    <BlockRoot
      as="section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        position: 'relative',
        // Breaks out to the true viewport edge via the page-bleed CSS
        // vars (SiteShellPresenter sets them so blocks adapt to
        // top-bar vs sidebar layouts).
        marginTop: 0,
        marginLeft: 'var(--page-bleed-margin, calc(50% - 50vw))',
        marginRight: 'var(--page-bleed-margin, calc(50% - 50vw))',
        marginBottom: 'clamp(20px, 4vw, 32px)',
        width: 'var(--page-bleed-width, 100vw)',
        maxWidth: 'var(--page-bleed-width, 100vw)',
        height: heightStyle,
        overflow: 'hidden',
        background: 'var(--color-neutral-900, #0c0a09)',
      }}
    >
      {list.map((s, i) => {
        const isActive = i === active;
        const align = s.align ?? 'left';
        const accent = s.accent ?? 'var(--color-brand-500, #2563eb)';
        return (
          <div
            key={i}
            aria-hidden={!isActive}
            inert={!isActive}
            style={{
              position: 'absolute',
              inset: 0,
              opacity: isActive ? 1 : 0,
              transition: 'opacity 800ms ease',
              pointerEvents: isActive ? 'auto' : 'none',
            }}
          >
            {s.backgroundImage && (
              <div
                className={isActive ? 'lms-hero-cinematic-active' : ''}
                style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage: `url("${s.backgroundImage}")`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            )}
            {/* Gradient overlay — heavier on the side text sits. */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  align === 'center'
                    ? 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.5) 100%)'
                    : 'linear-gradient(to right, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.55) 35%, rgba(0,0,0,0.15) 65%, transparent 90%)',
              }}
            />

            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: align === 'center' ? 'center' : 'flex-end',
                justifyContent: align === 'center' ? 'center' : 'flex-start',
                padding: 'clamp(40px, 6vw, 96px) clamp(20px, 6vw, 96px) clamp(72px, 8vw, 120px)',
                textAlign: align === 'center' ? 'center' : 'left',
              }}
            >
              <div style={{ maxWidth: 760, color: 'var(--color-text-on-brand, #fff)' }}>
                {s.kicker && (
                  <div
                    style={{
                      fontFamily: 'var(--font-family-body, inherit)',
                      fontSize: 'var(--font-size-xs, 12px)',
                      letterSpacing: 0,
                      textTransform: 'uppercase',
                      color: accent,
                      marginBottom: 'clamp(10px, 2vw, 16px)',
                      fontWeight: 600,
                    }}
                  >
                    {tx(s.kicker)}
                  </div>
                )}
                {s.title && (
                  <h1
                    style={{
                      margin: 0,
                      fontFamily: 'var(--font-family-display, inherit)',
                      fontSize: 'clamp(32px, 7vw, 84px)',
                      fontWeight: 700,
                      letterSpacing: 0,
                      lineHeight: 1.0,
                      textShadow: '0 4px 24px rgba(0,0,0,0.4)',
                    }}
                  >
                    {tx(s.title)}
                  </h1>
                )}
                {s.subtitle && (
                  <p
                    style={{
                      margin: 'clamp(14px, 2vw, 20px) 0 0',
                      fontSize: 'clamp(13px, 1.6vw, 19px)',
                      lineHeight: 1.5,
                      opacity: 0.92,
                      maxWidth: 580,
                      marginInline: align === 'center' ? 'auto' : undefined,
                    }}
                  >
                    {tx(s.subtitle)}
                  </p>
                )}
                {s.ctaLabel && (
                  <PortalLink
                    href={s.ctaHref ?? '#'}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 10,
                      marginTop: 'clamp(20px, 3vw, 28px)',
                      padding: 'clamp(10px, 2vw, 14px) clamp(20px, 4vw, 32px)',
                      background: 'var(--color-surface-card, #fff)',
                      color: 'var(--color-text-primary, #0c0a09)',
                      fontWeight: 600,
                      fontSize: 'clamp(13px, 1.5vw, 15px)',
                      textDecoration: 'none',
                      borderRadius: 'var(--radius-md, 8px)',
                      boxShadow: '0 4px 16px var(--color-shadow, rgba(0,0,0,0.3))',
                    }}
                  >
                    <span aria-hidden style={{ fontSize: 'var(--font-size-xs, 12px)' }}>
                      ▶
                    </span>
                    {tx(s.ctaLabel)}
                  </PortalLink>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {list.length > 1 && (
        <div
          style={{
            position: 'absolute',
            bottom: 'clamp(20px, 3vw, 36px)',
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            gap: 8,
            zIndex: 2,
          }}
        >
          {list.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Slide ${i + 1}`}
              onClick={() => {
                setActive(i);
                setPaused(true);
              }}
              style={{
                // Hit area meets the 24x24 CSS px touch-target minimum even
                // though the visual dot (below) stays small.
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: i === active ? 32 : 24,
                height: 24,
                border: 'none',
                background: 'transparent',
                padding: 0,
                cursor: 'pointer',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'block',
                  width: i === active ? 28 : 8,
                  height: 8,
                  borderRadius: 999,
                  background: i === active ? '#fff' : 'rgba(255,255,255,0.45)',
                  transition: 'width 240ms ease, background 240ms ease',
                }}
              />
            </button>
          ))}
        </div>
      )}

      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes hero-kenburns {
          0%   { transform: scale(1.0)  translate(0, 0); }
          100% { transform: scale(1.06) translate(-1%, -1%); }
        }
        .lms-hero-cinematic-active {
          animation: hero-kenburns 12s ease-in-out infinite alternate;
        }
        @media (prefers-reduced-motion: reduce) {
          .lms-hero-cinematic-active {
            animation: none;
          }
        }
      `,
        }}
      />
    </BlockRoot>
  );
}
